
import { computed, defineComponent, PropType, toRefs, watch, reactive, ref } from "vue";
import moment from "moment";
import { isSameDay } from "@/utils/util";

export default defineComponent({
  name: "UfDaterangePicker",
  props: {
    require: {
      type: String
    },
    type: {
      type: String,
      default: "date"
    },
    options: {
      type: Object as PropType<Approval.Dateslot>,
      default: () => ({})
    },
    show: Boolean,
    modelValue: {
      type: Array as PropType<Date[]>,
      required: true
    },
    minDateProps: {
      type: Date,
      default: () => moment("1970-01-01").toDate()
    },
    maxDateProps: {
      type: Date,
      default: () => moment("2070-01-01").toDate()
    },
    // 是否显示长期按钮
    isLongTerm: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:show", "update:modelValue", "clear", "confirm"],
  setup(props, { emit }) {
    const { show, modelValue, type, require, options, minDateProps, maxDateProps, isLongTerm } = toRefs(props);
    const longTermActive = ref(false);
    const format = type.value == "date" ? "YYYY.MM.DD" : "YYYY-MM-DD HH:mm";
    const data = reactive({
      popupShow: computed({
        get() {
          return show.value;
        },
        set(value) {
          emit("update:show", value);
        }
      }),
      currentPicker: "start",
      start: new Date(),
      end: new Date(),
      minDate: minDateProps.value,
      maxDate: maxDateProps.value
    });
    const startDateMaxDate = computed(() => {
      return require?.value === "date_insurance" ? maxDateProps.value : data.end;
    });
    watch(
      () => modelValue.value,
      now => {
        // 如果结束时间和最大一天是同一天，则是选择了长期
        if (isSameDay(moment(now[1]).format("YYYY-MM-DD"), "2099-12-31")) {
          longTermActive.value = true;
        }
        switch (require?.value) {
          case "date_insurance": {
            if (options.value.is_pass != "1") {
              const spaceNum = options.value.space_num || 24;
              data.start = now[0]
                ? new Date(now[0])
                : moment()
                    .add("3", "hours")
                    .toDate();
              data.end = now[1]
                ? new Date(now[1])
                : moment(data.start)
                    .add(spaceNum, "hours")
                    .toDate();
            } else {
              data.start = now[0] ? new Date(now[0]) : new Date();
              data.end = now[1] ? new Date(now[1]) : new Date();
            }
            data.minDate = moment(data.start).toDate();
            break;
          }
          case "business_date":
            data.start = now[0] ? new Date(now[0]) : new Date();
            data.end = now[1]
              ? new Date(now[1])
              : moment(data.start)
                  .add(1, "years")
                  .toDate();
            break;
          default:
            data.start = now[0] ? new Date(now[0]) : new Date();
            data.end = now[1] ? new Date(now[1]) : new Date();
            break;
        }
      },
      {
        immediate: true
      }
    );
    watch(
      () => data.start,
      now => {
        switch (require?.value) {
          case "date_insurance": {
            if (options.value.is_pass != "1") {
              const spaceNum = options.value.space_num || 24;
              data.end = moment(now)
                .add(spaceNum, "hours")
                .toDate();
              if (spaceNum > 24) {
                data.end = moment(now)
                  .add("24", "hours")
                  .toDate();
              } else {
                data.maxDate = data.end;
              }
            } else {
              data.end = moment(now)
                .add(1, "minutes")
                .toDate();
            }
            break;
          }
          default:
            break;
        }
      },
      {
        immediate: true
      }
    );
    watch(
      () => show.value,
      val => {
        if (val && longTermActive.value) {
          longTermActive.value = false;
        }
      }
    );
    const endMinDate = computed(() => {
      if (require?.value === "date_insurance") {
        return moment(data.start)
          .add(1, "minutes")
          .toDate();
      }
      return data.start;
    });
    const onClose = () => {
      data.popupShow = false;
    };
    const onConfirm = () => {
      emit("update:modelValue", [data.start, data.end]);
      emit("confirm");
      onClose();
    };
    const onClear = () => {
      emit("clear");
      if (isLongTerm.value) {
        longTermActive.value = true;
      }
      onClose();
    };
    const onLongTerm = () => {
      longTermActive.value = !longTermActive.value;

      if (longTermActive.value) {
        data.currentPicker = "start";
        data.end = new Date("2099-12-31");
      } else {
        data.end = data.start;
      }
    };

    return {
      ...toRefs(data),
      startDate: computed(() => moment(data.start).format(format)),
      endDate: computed(() => moment(data.end).format(format)),
      onConfirm,
      onClear,
      onClose,
      startDateMaxDate,
      endMinDate,
      longTermActive,
      onLongTerm
    };
  }
});
